/**
 * Parent Platform Authentication (Frontend)
 * 
 * Handles signed token authentication for iframe-embedded context.
 * Tokens are generated by the parent platform (TheSwitchIsOn) and
 * passed via URL parameter.
 */

interface ParentAuthPayload {
  email: string;
  name: string;
  exp: number;
  iat: number;
}

interface AuthTokenResult {
  email: string;
  name: string;
  token: string;
}

/**
 * Extracts and validates auth token from URL parameters
 * Token format: base64url(payload).base64url(signature)
 */
export function getAuthTokenFromUrl(): AuthTokenResult | null {
  if (typeof window === 'undefined') return null;

  const params = new URLSearchParams(window.location.search);
  const token = params.get('auth_token');

  if (!token) return null;

  try {
    const parts = token.split('.');
    if (parts.length !== 2) return null;

    const payloadB64 = parts[0];
    const payloadJson = base64UrlDecode(payloadB64);
    const payload: ParentAuthPayload = JSON.parse(payloadJson);

    // Check expiration (client-side check, server will verify too)
    const now = Math.floor(Date.now() / 1000);
    if (payload.exp < now) {
      console.warn('Auth token expired');
      return null;
    }

    return {
      email: payload.email,
      name: payload.name || payload.email.split('@')[0],
      token,
    };
  } catch (error) {
    console.error('Error parsing auth token:', error);
    return null;
  }
}

/**
 * Decodes base64url string
 */
function base64UrlDecode(str: string): string {
  const base64 = str.replace(/-/g, '+').replace(/_/g, '/');
  const padded = base64 + '='.repeat((4 - (base64.length % 4)) % 4);
  return atob(padded);
}

/**
 * Storage key for auth token
 */
const AUTH_TOKEN_STORAGE_KEY = 'bidsmart_auth_token';

/**
 * Stores the auth token for subsequent requests
 */
export function storeAuthToken(token: string): void {
  try {
    sessionStorage.setItem(AUTH_TOKEN_STORAGE_KEY, token);
  } catch {
    // Ignore storage errors
  }
}

/**
 * Gets stored auth token
 */
export function getStoredAuthToken(): string | null {
  try {
    return sessionStorage.getItem(AUTH_TOKEN_STORAGE_KEY);
  } catch {
    return null;
  }
}

/**
 * Clears stored auth token
 */
export function clearAuthToken(): void {
  try {
    sessionStorage.removeItem(AUTH_TOKEN_STORAGE_KEY);
  } catch {
    // Ignore storage errors
  }
}

/**
 * Gets auth headers for API requests
 * Uses signed token if available, falls back to email header
 */
export function getAuthHeaders(userEmail: string): Record<string, string> {
  const token = getStoredAuthToken();
  
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  };

  if (token) {
    headers['X-Auth-Token'] = token;
  } else {
    headers['X-User-Email'] = userEmail;
  }

  return headers;
}
